// Căn / sản phẩm (inventory): bảng giá của dự án (lọc, sắp ở server, trạng thái hiệu lực từ booking), chi tiết căn,
// lịch sử giá, loại căn, thông tin chủ nhà ký gửi (inventory.owner — mỗi lần đọc server ghi nhật ký, không đệm lâu).
// Màu / chip trạng thái căn dùng chung với Chốt căn (ST_COLORS, unitBadge của prototype).
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { inventory } from '@cathouse/contracts';
import { http, qk, type AnyQueryKey } from '@/shared/api';
import { UNIT_STATUS, syncServerTime } from '@/shared/lib';
import { StatusChip, type Tone } from '@/shared/ui';

export type UnitRow = inventory.UnitRow;
export type UnitList = inventory.UnitList;
export type UnitDetail = inventory.UnitDetail;
export type UnitStatus = inventory.UnitStatus;
export type UnitStatusCounts = inventory.UnitStatusCounts;
export type UnitSort = (typeof inventory.UNIT_SORTS)[number];
export type PriceHistoryEntry = inventory.PriceHistoryEntry;
export type UnitOwnerView = inventory.UnitOwnerView;

export interface UnitFilters {
  q?: string;
  status?: UnitStatus;
  block?: string;
  sort?: UnitSort;
  dir?: 'asc' | 'desc';
}

/** Bảng giá của dự án (tối đa 500 dòng — đủ cho một dự án; server lọc + sắp như prototype). */
export const useUnits = (projectId: string | null | undefined, f: UnitFilters, enabled = true) =>
  useQuery({
    queryKey: qk.inventory.units(projectId ?? '', f),
    queryFn: async () => {
      const r = await http.get<UnitList>(`/inventory/projects/${projectId}/units`, { query: { ...f, limit: 500 } });
      syncServerTime(r.serverTime);
      return r;
    },
    enabled: !!projectId && enabled,
    // Đổi bộ lọc trong cùng dự án: giữ bảng cũ tới khi có kết quả mới; đổi dự án: không hiện căn của dự án trước
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === projectId ? prev : undefined),
  });

export const useUnit = (id: string | null | undefined) => useQuery({ queryKey: qk.inventory.unit(id ?? ''), queryFn: () => http.get<UnitDetail>(`/inventory/units/${id}`), enabled: !!id });

export const useUnitPriceHistory = (id: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: qk.inventory.priceHistory(id ?? ''), queryFn: () => http.get<PriceHistoryEntry[]>(`/inventory/units/${id}/price-history`, { query: { limit: 20 } }), enabled: !!id && enabled });

/** Loại căn khác nhau (gợi ý ô "Loại", lọc nhu cầu khách). */
export const useUnitTypes = (projectId?: string | null, enabled = true) =>
  useQuery({ queryKey: qk.inventory.unitTypes(projectId ?? undefined), queryFn: () => http.get<string[]>('/inventory/unit-types', { query: { projectId: projectId ?? undefined } }), enabled, staleTime: 5 * 60_000 });

/**
 * Chủ nhà hàng ký gửi (inventory.owner). Mỗi lần đọc server ghi nhật ký truy cập ⇒ không tự tải lại khi quay lại tab,
 * không giữ trong bộ nhớ đệm sau khi đóng.
 */
export const useUnitOwner = (id: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.inventory.owner(id ?? ''),
    queryFn: () => http.get<UnitOwnerView>(`/inventory/units/${id}/owner`),
    enabled: !!id && enabled,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

// ---------- Làm mới sau khi ghi ----------
/** Tiền tố khoá bảng giá của một dự án (mọi bộ lọc). */
export const unitsOf = (projectId: string): AnyQueryKey => qk.inventory.units(projectId, {}).slice(0, 3);
/**
 * Khoá cần làm mới khi danh mục / giá / trạng thái căn đổi — mọi khoá inventory TRỪ chủ nhà (đọc lại = thêm một dòng
 * nhật ký truy cập) và yêu cầu xoá đang theo dõi.
 */
export const INVENTORY_LIVE_KEYS: readonly AnyQueryKey[] = [
  qk.inventory.projects(),
  qk.inventory.project('').slice(0, 2),
  qk.inventory.units('', {}).slice(0, 2),
  qk.inventory.unit('').slice(0, 2),
  qk.inventory.priceHistory('').slice(0, 2),
  qk.inventory.quote('', 0).slice(0, 2),
  qk.inventory.unitTypes().slice(0, 2),
  qk.inventory.overview(),
];
/** Sau thao tác ghi bảng hàng: inventory (trừ chủ nhà) + bảng hàng Chốt căn (giá, trạng thái, số trống). */
export function refreshInventory(qc: QueryClient) {
  return Promise.all([...INVENTORY_LIVE_KEYS, qk.booking.all].map((queryKey) => qc.invalidateQueries({ queryKey })));
}

// ---------- Hiển thị ----------
/** Màu đoạn trạng thái (thanh xếp chồng của thẻ dự án) — như ST_COLORS của prototype. */
export const ST_COLORS: Record<UnitStatus, string> = {
  open: 'var(--st-open-bar)',
  locked: 'var(--st-locked-line)',
  pending: 'var(--st-pending)',
  deposited: 'var(--st-deposited-bg)',
  contracted: 'var(--st-contracted-bg)',
  blocked: 'var(--st-blocked-fg)',
};
export const UNIT_STATUS_TONE: Record<UnitStatus, Tone> = { open: 'good', locked: 'warn', pending: 'violet', deposited: 'bad', contracted: 'bad', blocked: '' };
export const UNIT_STATUS_KEYS = Object.keys(UNIT_STATUS) as UnitStatus[];
export const UNIT_STATUS_OPTIONS: [UnitStatus, string][] = UNIT_STATUS_KEYS.map((k) => [k, UNIT_STATUS[k].label]);

/** Diện tích kiểu Việt Nam (75,5) — nay ở shared/lib; giữ export cho các import cũ từ entities/unit. */
export { fmtArea } from '@/shared/lib';

export function UnitStatusChip({ status }: { status: UnitStatus }) {
  return <StatusChip tone={UNIT_STATUS_TONE[status]}>{UNIT_STATUS[status].label}</StatusChip>;
}
/** Phần của thanh StatusStack theo số căn từng trạng thái. */
export const statusParts = (counts: UnitStatusCounts) => UNIT_STATUS_KEYS.map((k) => ({ label: UNIT_STATUS[k].label, value: counts[k] ?? 0, color: ST_COLORS[k] }));
